import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Tag from './block-tag';
import Badge from './block-badge';
import useDimensions from '../../../engine/use-dimensions';
import { blockConfig } from '../../../config';
import { renderLockTag } from './block-detail-popover';

const BlockStatusesWrapper = styled.div`
  padding: ${blockConfig.padding}px;
  height: 100%;
  font-size: 14px;
  letter-spacing: 1px;
  line-height: 16px;
  overflow: hidden;
  text-align: right;
`;

const Line = styled.div`
  min-height: 15px;
  margin-bottom: 1px;
  white-space: ${p => p.wrap ? 'wrap' : 'nowrap'};
`;

const BlockStatuses = ({
  status,
  badges,
  tags,
  isDraft,
  view,
  lock,
  isReservation,
  isAbsence,
  isRouteOptimisation,
  isInactive = false,
  isAssignedPlanner,
  hasError,
  statusWidth,
  isBlockLoading,
}) => {
  const statusRef = useRef(null);
  const tagsRef = useRef(null);

  const [showShortText, setShowShortText] = useState(false);
  const [tagsRefFullWidth, setTagsRefFullWidth] = useState(0);

  const [tagsRefSize] = useDimensions(tagsRef);

  useEffect(() => {
    const blockRefWidth = statusWidth - 5;

    if (statusWidth) {
      if (tagsRefFullWidth && (tagsRefFullWidth >= blockRefWidth)) {
        setShowShortText(true);
      } else {
        setShowShortText(false);
      }
    }
  }, [statusWidth, tagsRefFullWidth]);


  useEffect(() => {
    if (tagsRefSize.width && !tagsRefFullWidth) {
      setTagsRefFullWidth(tagsRefSize.width);
    }
  }, [tagsRefSize.width]);

  const getTagColor = () => {
    if (isInactive) {
      return blockConfig.inactiveColor.main;
    }
    if (isAbsence) {
      return blockConfig.absenceColor.main;
    }
    if (isRouteOptimisation) {
      return blockConfig.routeOptimisationColor.main;
    }
    return blockConfig.reservationColor.main;
  };

  const renderTags = () => {
    if (isDraft && view !== blockConfig.statusesView.one) {
      return (
        <Tag
          color={blockConfig.alert[status].colorStatus}
          tagIcon={blockConfig.alert[status].icon}
          text={blockConfig.alert[status].text}
          showIconOnly={true}
        />
      );
    } else if (!isDraft) {
      if (view !== blockConfig.statusesView.one) {
        return (
          <Tag
            color={blockConfig.alert[status].colorStatus}
            tagIcon={blockConfig.alert[status].icon}
            text={blockConfig.alert[status].text}
            showIconOnly={statusWidth < 85}
          />
        );
      }
      return (
        <Tag
          color={blockConfig.alert[status].colorStatus}
          tagIcon={blockConfig.alert[status].icon}
          showIconOnly={true}
        />
      );
    }
    return null;
  };

  const isPlanningBlock =
    !isReservation && !isAbsence && !isRouteOptimisation;

  return (
    <BlockStatusesWrapper>
      <Line>
        <span ref={statusRef}>

          {renderLockTag(lock, isAssignedPlanner, hasError, isDraft, isBlockLoading)}

          {status &&
            blockConfig.alert[status] &&
            status !== 'none' &&
            renderTags()
          }
          {!isPlanningBlock &&
            (
              (view === blockConfig.statusesView.one && !isDraft) ||
              view === blockConfig.statusesView.two ||
              view === blockConfig.statusesView.full
            ) && badges?.length &&
            <Tag
              color={getTagColor()}
              tagIcon={badges[0]?.icon}
              text=''
              lightIcon={isAbsence}
            />
          }
        </span>
      </Line>
      {isPlanningBlock &&
        view !== blockConfig.statusesView.one && badges &&
        <Line>
          {/** badges */}
          {badges?.map((badge, key) =>
            <Badge key={key} icon={badge.icon} iconStyle={badge.iconStyle} />,
          )}
        </Line>
      }
      {isPlanningBlock && tags &&
        <Line wrap={view === blockConfig.statusesView.one}>
          <span ref={tagsRef}>
            {/** tags */}
            {tags
              ?.filter((tag, index) => {
                if (view !== blockConfig.statusesView.full) {
                  if (index < blockConfig.statusesView.two) {
                    return true;
                  }
                  return false;
                }
                return true;
              })
              ?.map((tag, key) =>
                <Tag
                  color={blockConfig.colors.default}
                  text={tag.description}
                  shortText={tag.shortDescription}
                  showShortText={showShortText}
                  key={key}
                />,
              )}
          </span>
        </Line>
      }
    </BlockStatusesWrapper>
  );
};

BlockStatuses.propTypes = {
  status: PropTypes.object,
  badges: PropTypes.array,
  tags: PropTypes.array,
  isDraft: PropTypes.bool,
  view: PropTypes.string,
  isReservation: PropTypes.bool,
  isAbsence: PropTypes.bool,
  isRouteOptimisation: PropTypes.bool,
  isInactive: PropTypes.bool,
  isAssignedPlanner: PropTypes.bool,
  hasError: PropTypes.bool,
  statusWidth: PropTypes.number,
  lock: PropTypes.object,
  isBlockLoading: PropTypes.bool,
};


export default BlockStatuses;
